import { useNavigate } from "react-router-dom";
import { StatusBadge } from "@/components/common";

export const inr = (v) => `₹${Number(v || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
export const errMsg = (e, fb) => { const d = e.response?.data?.detail; return typeof d === "string" ? d : d?.message || fb; };

export function Tile({ label, value, sub, tone = "default", testid }) {
  const tones = { default: "text-foreground", green: "text-signal-green", amber: "text-signal-amber", red: "text-signal-red", blue: "text-primary" };
  return (
    <div className="bg-card border border-border rounded-lg p-3 sm:p-4" data-testid={testid}>
      <p className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">{label}</p>
      <p className={`metric text-xl sm:text-2xl font-extrabold ${tones[tone]}`}>{value}</p>
      {sub && <p className="text-xs text-muted-foreground">{sub}</p>}
    </div>
  );
}

export function DashTiles({ d }) {
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-3" data-testid="reimb-dash-tiles">
      <Tile label="Total claims" value={d.total_claims} sub={inr(d.total_amount)} testid="tile-total" />
      <Tile label="Pending approval" value={d.pending_claims} sub={inr(d.pending_amount)} tone={d.pending_claims ? "amber" : "default"} testid="tile-pending" />
      <Tile label="Approved" value={d.approved_claims} tone="green" testid="tile-approved" />
      <Tile label="Rejected" value={d.rejected_claims} tone={d.rejected_claims ? "red" : "default"} testid="tile-rejected" />
      <Tile label="Returned" value={d.returned_claims} tone={d.returned_claims ? "amber" : "default"} testid="tile-returned" />
      <Tile label="Payment pending" value={d.payment_pending} sub={inr(d.payment_pending_amount)} tone="blue" testid="tile-payment-pending" />
      <Tile label="Paid amount" value={inr(d.paid_amount)} tone="green" testid="tile-paid" />
      <Tile label="Outstanding" value={inr(d.outstanding_amount)} tone={d.outstanding_amount ? "amber" : "default"} testid="tile-outstanding" />
      <Tile label="On hold" value={d.on_hold} tone={d.on_hold ? "red" : "default"} testid="tile-hold" />
      <div className="bg-card border border-border rounded-lg p-3" data-testid="tile-ageing">
        <p className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Ageing (open)</p>
        {["0-7", "8-15", "16-30", "31-60", "60+"].map((b) => (
          <p key={b} className="text-xs flex justify-between"><span className="text-muted-foreground">{b}d</span><span className="metric">{d.ageing?.[b]?.count || 0} · {inr(d.ageing?.[b]?.amount)}</span></p>))}
      </div>
    </div>
  );
}

export function ClaimTable({ rows, testid = "reimb-table", showEmployee = true }) {
  const navigate = useNavigate();
  return (
    <div className="bg-card border border-border rounded-lg overflow-x-auto">
      <table className="w-full text-sm" data-testid={testid}>
        <thead><tr className="border-b border-border text-left">
          {["Claim", showEmployee && "Employee", "Department", "Submitted", "Status", "Step", "Total", "Net payable", "Outstanding", "Age"].filter(Boolean).map((h) => (
            <th key={h} className="px-3 py-2.5 text-[10px] font-mono uppercase tracking-widest text-muted-foreground whitespace-nowrap">{h}</th>))}
        </tr></thead>
        <tbody>
          {rows.map((c) => (
            <tr key={c.claim_id} className="border-b border-border/50 hover:bg-accent/40 cursor-pointer" onClick={() => navigate(`/reimbursements/${c.claim_id}`)} data-testid={`claim-row-${c.claim_id}`}>
              <td className="px-3 py-2.5 font-mono text-xs text-primary font-bold">{c.claim_id}</td>
              {showEmployee && <td className="px-3 py-2.5">{c.employee?.name}</td>}
              <td className="px-3 py-2.5 text-xs text-muted-foreground">{c.employee?.department || "—"}</td>
              <td className="px-3 py-2.5 text-xs">{(c.submitted_at || c.created_at || "").slice(0, 10)}</td>
              <td className="px-3 py-2.5"><StatusBadge status={c.status} testid={`claim-status-${c.claim_id}`} /></td>
              <td className="px-3 py-2.5 text-xs text-muted-foreground">{c.current_step_label || "—"}</td>
              <td className="px-3 py-2.5 metric text-xs">{inr(c.total_claim)}</td>
              <td className="px-3 py-2.5 metric text-xs font-bold">{inr(c.net_payable)}</td>
              <td className="px-3 py-2.5 metric text-xs text-signal-amber">{inr(c.outstanding)}</td>
              <td className="px-3 py-2.5 metric text-xs">{c.ageing_days}d</td>
            </tr>))}
          {rows.length === 0 && <tr><td colSpan={10} className="px-4 py-8 text-center text-sm text-muted-foreground">No claims here.</td></tr>}
        </tbody>
      </table>
    </div>
  );
}
